import { Injectable, inject } from '@angular/core';
import { BiometricService } from './biometric.service';
import { ApiService } from './api.service';

const STORAGE_KEY = 'cogitationes_auth';

export interface AuthUser {
  email: string;
  token: string;
  firstName?: string;
  lastName?: string;
  organizationCode?: string;
  phone?: string;
  school?: string;
  major?: string;
  isAdmin?: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly biometric = inject(BiometricService);
  private readonly api = inject(ApiService);
  private user: AuthUser | null = this._loadFromStorage();

  setUser(data: { user: { email: string; firstName?: string; lastName?: string; organizationCode?: string; phone?: string; school?: string; major?: string; isAdmin?: boolean }; token: string }) {
    this.user = {
      email: data.user.email,
      token: data.token,
      firstName: data.user.firstName,
      lastName: data.user.lastName,
      organizationCode: data.user.organizationCode,
      phone: data.user.phone,
      school: data.user.school,
      major: data.user.major,
      isAdmin: data.user.isAdmin,
    };
    this._saveToStorage();
  }

  getUser(): AuthUser | null {
    if (!this.user) {
      this.user = this._loadFromStorage();
    }
    return this.user;
  }

  /**
   * Record logout on the server (browser info) then clear all persistent auth data.
   * Clears: in-memory user, localStorage, sessionStorage, and stored biometric credentials.
   * Callback runs after sign-off (and biometric clear) completes.
   */
  logout(callback?: () => void): void {
    const browserInfo = this.getBrowserInfo();
    this.api.logout(browserInfo).subscribe({
      next: () => {
        this.signOff().then(() => callback?.());
      },
      error: () => {
        this.signOff().then(() => callback?.());
      },
    });
  }

  private getBrowserInfo(): string {
    if (typeof navigator === 'undefined') return '';
    return [navigator.userAgent, navigator.platform].filter(Boolean).join(' | ');
  }

  /**
   * Clear all persistent auth data: in-memory user, localStorage, sessionStorage,
   * and native biometric credentials. Returns a promise that resolves when done.
   */
  async signOff(): Promise<void> {
    this.user = null;
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {
      // ignore storage errors
    }
    await this.biometric.clearCredentials();
  }

  isLoggedIn(): boolean {
    return !!this.getUser();
  }

  /**
   * Refresh user profile from the backend (e.g. organizationCode).
   * Keeps the existing token; updates email, firstName, lastName, organizationCode from /api/auth/me.
   */
  refreshUser(): void {
    const current = this.getUser();
    if (!current?.token) return;
    this.api.getMe().subscribe({
      next: (res) => {
        if ('error' in res) return;
        this.user = {
          ...current,
          email: res.user.email,
          firstName: res.user.firstName,
          lastName: res.user.lastName,
          organizationCode: res.user.organizationCode,
          phone: res.user.phone,
          school: res.user.school,
          major: res.user.major,
          isAdmin: res.user.isAdmin,
        };
        this._saveToStorage();
      },
    });
  }

  private _saveToStorage() {
    if (this.user) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.user));
    }
  }

  private _loadFromStorage(): AuthUser | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as AuthUser;
    } catch {
      return null;
    }
  }
}
